/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useTheme } from '@mui/material';
import type { EChartsInstance } from 'echarts-for-react';
import React from 'react';

/** One clock per view; standalone charts can use their own clock. Times are milliseconds. */
export const useCurrentTime = (enabled = true): number => {
  const [now, setNow] = React.useState(Date.now);
  React.useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const update = (): void => setNow(Date.now());
    update();
    const timer = window.setInterval(update, 60_000);
    return (): void => window.clearInterval(timer);
  }, [enabled]);
  return now;
};

interface IndicatorLine {
  id: string;
  x: number;
  y: number;
  height: number;
}

// Isolate the ECharts model/view APIs used to read the rendered geometry here.
// Read the slider background itself: the component bounds include handles and
// labels, and the preview can be translated relative to its configured layout.
interface ChartGeometryApi {
  getModel: () => {
    eachComponent: (
      type: 'xAxis',
      callback: (axis: {
        componentIndex: number;
        axis: {
          scale: {
            getExtent: () => [number, number];
            isBlank: () => boolean;
          };
        };
        get: (key: 'gridIndex') => number;
      }) => void,
    ) => void;
    getComponent: (
      type: 'grid' | 'dataZoom',
      index: number,
    ) =>
      | {
          coordinateSystem?: {
            getRect: () => {
              x: number;
              y: number;
              width: number;
              height: number;
            };
          };
          get: (key: 'show') => boolean;
          getAxisProxy?: (
            dimension: 'x',
            index: number,
          ) => { _extent: [number, number] } | undefined;
        }
      | undefined;
  };
  getViewOfComponentModel: (model: unknown) =>
    | {
        _displayables?: {
          sliderGroup?: {
            childAt: (index: number) => {
              shape: { width: number; height: number };
              getComputedTransform: () => unknown;
              transformCoordToGlobal: (x: number, y: number) => number[];
            };
          };
        };
      }
    | undefined;
}

export const getCurrentTimeLines = (
  instance: EChartsInstance,
  currentTime: number,
  showPlots: boolean,
  showZoom: boolean,
): IndicatorLine[] => {
  if (instance.isDisposed() || !Number.isFinite(currentTime)) {
    return [];
  }
  const api = instance as unknown as ChartGeometryApi;
  const model = api.getModel();
  if (!model) {
    return [];
  }
  const lines: IndicatorLine[] = [];
  model.eachComponent('xAxis', (axis) => {
    if (axis.axis.scale.isBlank()) {
      return;
    }
    const [min, max] = axis.axis.scale.getExtent();
    if (showPlots && currentTime >= min && currentTime <= max) {
      const rect = model
        .getComponent('grid', axis.get('gridIndex'))
        ?.coordinateSystem?.getRect();
      const x = instance.convertToPixel(
        { xAxisIndex: axis.componentIndex },
        currentTime,
      );
      if (
        rect &&
        Number.isFinite(x) &&
        x >= rect.x &&
        x <= rect.x + rect.width
      ) {
        lines.push({
          id: `plot-${axis.componentIndex}`,
          x,
          y: rect.y,
          height: rect.height,
        });
      }
    }
    if (showZoom && axis.componentIndex === 0) {
      const zoom = model.getComponent('dataZoom', 0);
      const view =
        zoom && zoom.get('show') && api.getViewOfComponentModel(zoom);
      const { _displayables: displayables } = view || {};
      const background = displayables?.sliderGroup?.childAt(0);
      // The proxy retains the full axis extent while the scale is zoomed.
      const { _extent: extent } =
        zoom?.getAxisProxy?.('x', axis.componentIndex) ?? {};
      if (
        background &&
        extent &&
        extent.every(Number.isFinite) &&
        currentTime >= extent[0] &&
        currentTime <= extent[1]
      ) {
        background.getComputedTransform();
        const start = background.transformCoordToGlobal(0, 0);
        const end = background.transformCoordToGlobal(
          background.shape.width,
          background.shape.height,
        );
        const fraction =
          extent[0] === extent[1]
            ? 0.5
            : (currentTime - extent[0]) / (extent[1] - extent[0]);
        lines.push({
          id: 'zoom',
          x: start[0] + fraction * (end[0] - start[0]),
          y: Math.min(start[1], end[1]),
          height: Math.abs(end[1] - start[1]),
        });
      }
    }
  });
  return lines;
};

export const CurrentTime: React.FC<{
  instance: EChartsInstance | null;
  currentTime: number;
  showPlots: boolean;
  showZoom: boolean;
}> = ({ instance, currentTime, showPlots, showZoom }) => {
  const theme = useTheme();
  const [lines, setLines] = React.useState<IndicatorLine[]>([]);
  React.useEffect(() => {
    if (!instance) {
      return undefined;
    }
    const update = (): void => {
      const next = getCurrentTimeLines(
        instance,
        currentTime,
        showPlots,
        showZoom,
      );
      setLines((previous) =>
        JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
      );
    };
    update();
    instance.on('rendered', update);
    return (): void => {
      if (!instance.isDisposed()) {
        instance.off('rendered', update);
      }
    };
  }, [instance, currentTime, showPlots, showZoom]);
  const { playerNeedleTime, playerNeedleTimeTop } =
    theme.palette.geowebColors.timeSlider;
  return (
    <svg
      aria-hidden="true"
      data-testid="CurrentTime"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {lines.map(({ id, x, y, height }) => (
        <g key={id}>
          <line
            stroke={playerNeedleTime.rgba}
            strokeWidth={7}
            x1={x}
            x2={x}
            y1={y}
            y2={y + height}
          />
          <line
            stroke={playerNeedleTimeTop.rgba}
            strokeWidth={2}
            x1={x}
            x2={x}
            y1={y}
            y2={y + height}
          />
        </g>
      ))}
    </svg>
  );
};
